import React, { useState, useEffect, useRef } from 'react';
import { Button, Dropdown, Image, Stack } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { useNavigate } from 'react-router-dom';
import { S3_URL } from 'constants/index';
import parse from 'html-react-parser';
import { sanitizeHtml } from 'components/sanitizeHtml';
import { formatDate, getCreatedDifferenceInBestUnit, unitNumber } from 'utils';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';
import useCommonStore, { setModalConfig } from 'store/common';
import { useDeleteJobMutation } from 'services';
import toast from 'react-hot-toast';
import './styles.scss';

const InactiveExpireJobList: React.FC<any> = ({
	job,
	refetch = () => null,
	enableKebabIcon = false,
	isApplicants = false,
	isApplictionBoard = false,
	isRenew = false,
	startDate = '',
	endDate = '',
	setInactive = () => null,
}) => {
	const formattedStartDate = formatDate(startDate);
	const formattedEndDate = formatDate(endDate);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const { mutateAsync: DeleteJob } = useDeleteJobMutation();
	const [hasMore, setHasMore] = useState(false);
	const descriptionRef = useRef(null);

	useEffect(() => {
		const element: any = descriptionRef?.current;
		if (element?.scrollHeight > element?.clientHeight) {
			setHasMore(true);
		}
	}, [job?.description]);

	const { token, user: authUser }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	const onCardClick = (id: number) => {
		navigate(`/job-detail/${id}`);
	};

	const onHandleDelete = (id: number | null = null) => {
		setModalConfig({
			visible: true,
			id: id,
			type: 'confirmModal',
			onClick: () => onHandleYes(id),
			data: {
				title: translate('Modal.wantToDelete', { title: `${translate('Modal.job')}` }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleYes = (id: number | null) => {
		DeleteJob(id)
			.then((res: any) => {
				if (res?.success) {
					toast.success(res?.message);
					refetch();
				} else {
					toast.error(res?.message);
				}
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	return (
		<Stack className="posted-job-box mb-3 mb-xxl-4" onClick={() => onCardClick(job?.job_id)}>
			<div className="posted-job-box-content d-flex gap-3 gap-xl-5 position-relative">
				<Stack className="flex-1 gap-3">
					<div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
						<div className="company-logo">
							<Image
								src={
									job?.companyLogo ? `${S3_URL}${job.companyLogo}` : IMAGE_PATHS.CompanyLogoDefault
								}
								alt="logo"
								className="w-100 h-100 object-fit-cover"
								loading="lazy"
							/>
						</div>
						<Stack className="job-detail justify-content-center flex-1">
							{job?.title && <h4 className="mb-0 fw-bold">{job.title}</h4>}
							{job?.companyName && <p className="company-name mb-0">{job.companyName}</p>}
						</Stack>
					</div>
					<div className="job-duration d-flex gap-3 gap-xl-5">
						<p className="mb-0">
							{translate('Extras.startDate')}: {formattedStartDate}{' '}
						</p>
						<p className="mb-0">
							{translate('Extras.endDate')}: {formattedEndDate}{' '}
						</p>
					</div>
					{job?.description?.length > 0 && (
						<div className="posted-job-box-description">
							<p
								ref={descriptionRef}
								className={`mb-0 description-truncated ${hasMore ? 'has-more' : ''}`}
							>
								{parse(sanitizeHtml(job?.description))}
							</p>
						</div>
					)}
				</Stack>

				<div className="posted-job-actions">
					<Stack className="gap-2 gap-md-3 align-items-end">
						<div className="edit-action">
							<Dropdown onClick={(evt) => evt.stopPropagation()}>
								<Dropdown.Toggle
									variant="success"
									id="dropdown-basic"
									className="p-0 border-0 rounded-circle"
								>
									<i className="ri-more-2-fill"></i>
								</Dropdown.Toggle>

								<Dropdown.Menu>
									{authUser?.userId === job?.companyId && (
										<>
											<Dropdown.Item onClick={() => navigate(`/company/edit-job/${job?.jobId}`)}>
												<i className="ri-edit-line"></i> {translate('Common.edit')}
											</Dropdown.Item>
											<Dropdown.Item onClick={() => onHandleDelete(job?.jobId)}>
												<i className="ri-delete-bin-7-line"></i> {translate('Common.delete')}
											</Dropdown.Item>
										</>
									)}
									{authUser?.userId !== job?.companyId && (
										<>
											<Dropdown.Item href="#/action-2">
												<i className="ri-flag-line"></i> {translate('Report.report')}
											</Dropdown.Item>
											<Dropdown.Item href="#/action-3">
												<i className="ri-spam-2-line"></i> {translate('Report.block')}
											</Dropdown.Item>
										</>
									)}
								</Dropdown.Menu>
							</Dropdown>
						</div>
						{isRenew && token && (
							<Button
								className="filled-btn rounded-pill lh-1"
								onClick={(e) => {
									e.stopPropagation();
									setInactive(job?.job_id);
								}}
							>
								{translate('Company.renewJob')}
							</Button>
						)}
						{isApplicants && token && (
							<Button
								className="transparent-btn rounded-pill lh-1"
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/company/applicants/${job?.jobId}`);
								}}
							>
								{translate('Company.applicants')}
							</Button>
						)}
						{isApplictionBoard && (
							<Button className="filled-btn rounded-pill lh-1" onClick={() => {}}>
								{translate('Company.applicationBoard')}
							</Button>
						)}
					</Stack>
				</div>

				{enableKebabIcon && (
					<div className="edit-action">
						<Dropdown onClick={(evt) => evt.stopPropagation()}>
							<Dropdown.Toggle
								variant="success"
								id="dropdown-basic"
								className="p-0 border-0 rounded-circle"
							>
								<i className="ri-more-2-fill"></i>
							</Dropdown.Toggle>

							<Dropdown.Menu>
								{authUser?.userId === job?.companyId && (
									<>
										<Dropdown.Item onClick={() => navigate(`/company/edit-job/${job?.jobId}`)}>
											<i className="ri-edit-line"></i> {translate('Common.edit')}
										</Dropdown.Item>
										<Dropdown.Item onClick={() => onHandleDelete(job?.jobId)}>
											<i className="ri-delete-bin-7-line"></i> {translate('Common.delete')}
										</Dropdown.Item>
									</>
								)}
								{authUser?.userId !== job?.companyId && (
									<>
										<Dropdown.Item href="#/action-2">
											<i className="ri-flag-line"></i> {translate('Report.report')}
										</Dropdown.Item>
										<Dropdown.Item href="#/action-3">
											<i className="ri-spam-2-line"></i> {translate('Report.block')}
										</Dropdown.Item>
									</>
								)}
							</Dropdown.Menu>
						</Dropdown>
					</div>
				)}
			</div>

			<div className="posted-job-box-basic-info d-flex flex-wrap align-items-center">
				{job?.jobType && (
					<div className="job-info d-flex align-items-center gap-2">
						<i className="ri-time-line"></i>
						<h6 className="mb-0 fw-bold">{job.jobType}</h6>
					</div>
				)}
				<span className="info-separator"></span>
				<div className="job-info d-flex align-items-center gap-2">
					<i className="ri-wallet-3-line"></i>
					<h6 className="mb-0 fw-bold">
						{job?.currency}
						{[unitNumber(job?.minAmount, 1), unitNumber(job?.maxAmount, 1)]
							.filter(Boolean)
							.join('-') ?? ''}
						<span className="ps-1">{job?.salaryType && `(${job.salaryType})`}</span>
					</h6>
					<span className="info-separator"></span>
				</div>

				<div className="job-info d-flex align-items-center gap-2">
					<i className="ri-group-line"></i>
					<h6 className="mb-0 fw-bold">{unitNumber(job.totalJobApply, 0)}</h6>
				</div>
				<span className="info-separator"></span>

				{job?.level?.title && (
					<>
						<div className="job-info d-flex align-items-center gap-2">
							<i className="ri-award-line"></i>
							<h6 className="mb-0 fw-bold">{job?.level?.title}</h6>
						</div>
						<span className="info-separator"></span>
					</>
				)}

				<div className="job-info d-flex align-items-center gap-2">
					<i className="ri-list-check-2"></i>
					<h6 className="mb-0 fw-bold">
						{translate('Extras.assessment')}:{' '}
						{job?.isJobAssessment ? translate('Common.yes') : translate('Common.no')}
					</h6>
				</div>
				<span className="info-separator"></span>

				{job?.createdAt && (
					<div className="job-info d-flex align-items-center gap-2">
						<i className="ri-calendar-line"></i>
						<h6 className="mb-0 fw-bold">{getCreatedDifferenceInBestUnit(job.createdAt)}</h6>
					</div>
				)}
			</div>
			{enableKebabIcon && authUser?.userId === job?.companyId && (
				<div className="posted-job-box-actions d-flex gap-2 gap-sm-3 justify-content-sm-end mt-auto">
					{job.totalJobApply > 0 && (
						<>
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/company/applicants/${job?.jobId}`);
								}}
								className="transparent-btn rounded-pill lh-1"
							>
								{translate('Company.applicants')}
							</Button>
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/company/hiring-board/${job?.jobId}`);
								}}
								className="filled-btn rounded-pill lh-1"
							>
								{translate('Company.applicationBoard')}
							</Button>
						</>
					)}
				</div>
			)}
		</Stack>
	);
};

export default React.memo(InactiveExpireJobList);
